import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next";

const ApartmentDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [apartment, setApartment] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [bookings, setBookings] = useState([]);
  const [dailyPrices, setDailyPrices] = useState({});
  const [currencies, setCurrencies] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const getApiBaseUrl = () =>
    process.env.REACT_APP_API_URL || "http://localhost:8000";

  // Fetch apartment and location
  useEffect(() => {
    const token = localStorage.getItem("token");
    let userGroups = [];
    const rawGroups = localStorage.getItem("groups");

    if (rawGroups) {
      try {
        const parsed = JSON.parse(rawGroups);
        userGroups = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        userGroups = [rawGroups];
      }
    }

    setIsLoggedIn(!!token);
    setIsOwner(userGroups.some(g => String(g).trim() === "Owners"));

    fetch(`${getApiBaseUrl()}/api/properties/apartments/${id}/`)
      .then((res) => res.json())
      .then((data) => {
        setApartment(data);
        fetch(`${getApiBaseUrl()}/api/locations/${data.location}/`)
          .then((res) => res.json())
          .then((locationData) => setLocationName(locationData.name))
          .catch((err) => console.error("Error fetching location:", err));
      })
      .catch((err) => console.error("Error fetching apartment:", err));
  }, [id]);

  // Fetch bookings
  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/properties/apartments/${id}/bookings/`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => setBookings(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Error fetching bookings:", err));
  }, [id]);

  // Fetch currencies and build a map
  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/currencies/`)
      .then((res) => res.json())
      .then((data) => {
        const map = {};
        data.forEach((c) => {
          map[c.id] = { symbol: c.symbol, code: c.code };
        });
        setCurrencies(map);
      })
      .catch((err) => console.error("Error fetching currencies:", err));
  }, []);

  // Fetch daily prices
  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/properties/apartments/${id}/daily-prices/`)
      .then((res) => res.json())
      .then((data) => {
        const mappedPrices = {};
        data.forEach((item) => {
          mappedPrices[item.date] = {
            price: item.price,
            currencyId: item.currency, // currency ID
          };
        });
        setDailyPrices(mappedPrices);
      })
      .catch((err) => console.error("Error fetching daily prices:", err));
  }, [id]);

  // Parse a "YYYY-MM-DD" string as a LOCAL date (matching how react-calendar
  // constructs tile dates), instead of new Date(str) which treats a bare
  // date string as UTC midnight and shifts the comparison by a day in any
  // timezone ahead of UTC (e.g. CEST) — checkout day was wrongly blocked
  // and check-in day wrongly left free as a result.
  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const isBooked = (date) =>
    bookings.some((b) => {
      const start = parseLocalDate(b.start_date);
      const end = parseLocalDate(b.end_date);
      // end_date is exclusive (checkout day) — do not mark it as blocked
      return date >= start && date < end;
    });

  const formatDate = (date) =>
    date
      .toLocaleDateString("de-DE", { year: "numeric", month: "2-digit", day: "2-digit" })
      .split(".")
      .reverse()
      .join("-");

  const tileContent = ({ date, view }) => {
    if (view === "month") {
      const formattedDate = formatDate(date);
      const daily = dailyPrices[formattedDate];
      if (daily && daily.price != null) {
        const symbol = currencies[daily.currencyId]?.symbol || "";
        return <p className="text-xs mt-1">{symbol}{daily.price}</p>;
      }
      return null;
    }
  };

  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      return isBooked(date) ? "booked" : "";
    }
  };

  const handleFileChange = (e) => setSelectedFile(e.target.files[0]);

  const handleUploadPicture = async () => {
    if (!selectedFile) {
      setError(t("Please select a file to upload."));
      return;
    }
    setIsUploading(true);
    setError("");
    const formData = new FormData();
    formData.append("image", selectedFile);
    formData.append("size_in_bytes", selectedFile.size);
    formData.append("format", selectedFile.type.split("/")[1].toUpperCase());
    formData.append("apartment", id);

    try {
      const res = await fetch(`${getApiBaseUrl()}/api/pictures/`, {
        method: "POST",
        headers: { Authorization: `Token ${localStorage.getItem("token")}` },
        body: formData,
      });

      if (!res.ok) throw new Error(t("Failed to upload picture."));
      const newPic = await res.json();

      setApartment((prev) => ({
        ...prev,
        pictures: [
          ...prev.pictures,
          {
            ...newPic,
            image: newPic.image.startsWith("http")
              ? newPic.image
              : `${getApiBaseUrl()}${newPic.image}`,
          },
        ],
      }));

      setSelectedFile(null);
      alert(t("Picture uploaded successfully."));
    } catch (err) {
      console.error(err);
      setError(t("Failed to upload picture. Please try again."));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeletePicture = async (pictureId) => {
    if (!window.confirm(t("Are you sure you want to delete this picture?"))) return;

    try {
      const res = await fetch(`${getApiBaseUrl()}/api/pictures/${pictureId}/`, {
        method: "DELETE",
        headers: { Authorization: `Token ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error(t("Failed to delete picture."));

      setApartment((prev) => ({
        ...prev,
        pictures: prev.pictures.filter((p) => p.id !== pictureId),
      }));
      alert(t("Picture deleted successfully."));
    } catch (err) {
      console.error(err);
      setError(t("Failed to delete picture. Please try again."));
    }
  };

  if (!apartment) return <div className="min-h-screen flex items-center justify-center">{t("loading")}</div>;

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1 pt-20 pb-20 px-3 md:px-8 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl w-full mx-auto py-4">
          {/* Apartment Info */}
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md">
            <h1 className="text-2xl md:text-3xl font-bold mb-4">{apartment.apartment_name}</h1>
            <p className="text-lg mb-2"><span className="font-semibold">{t("location")}:</span> {locationName}</p>
            <p className="text-lg mb-2"><span className="font-semibold">{t("rooms")}:</span> {apartment.rooms}</p>
            <p className="text-lg mb-2"><span className="font-semibold">{t("size")}:</span> {apartment.size} sqm</p>
            <h2 className="text-xl font-bold mt-4 mb-2">{t("availability")}</h2>
            <Calendar
              className="custom-calendar w-full max-w-full overflow-x-auto"
              tileClassName={tileClassName}
              tileContent={tileContent}
            />
          </div>

          {/* Pictures */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-bold mb-4">{t("pictures")}</h2>
            <div className="w-full">
              {apartment.pictures.length > 0 ? (
                <Swiper
                  modules={[Navigation]}
                  spaceBetween={20}
                  slidesPerView={1}
                  navigation
                  loop={true}
                  className="rounded-lg shadow-lg"
                  breakpoints={{ 640: { slidesPerView: 1 }, 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
                >
                  {apartment.pictures.map((picture) => (
                    <SwiperSlide key={picture.id} className="relative">
                      <img
                        src={picture.image.startsWith("http") ? picture.image : `${getApiBaseUrl()}${picture.image}`}
                        alt={picture.tags?.join(", ") || "Apartment picture"}
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      {isLoggedIn && isOwner && (
                        <button
                          onClick={() => handleDeletePicture(picture.id)}
                          className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded hover:bg-red-700"
                        >
                          {t("Delete")}
                        </button>
                      )}
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : <p className="text-center text-gray-500">{t("no_images")}</p>}
            </div>

            {isLoggedIn && isOwner && (
              <div className="mt-4">
                <h3 className="text-lg font-bold mb-2">{t("Upload Picture")}</h3>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="mb-2 w-full border rounded p-2"
                  accept="image/*"
                />
                <button
                  onClick={handleUploadPicture}
                  disabled={isUploading}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-blue-300 w-full"
                >
                  {isUploading ? t("Uploading...") : t("Upload")}
                </button>
                {error && <p className="text-red-500 mt-2">{error}</p>}
              </div>
            )}

            {!isOwner && isLoggedIn && (
              <p className="text-sm text-gray-500 mt-4 italic">{t("Only owners can upload or delete pictures.")}</p>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ApartmentDetails;